/**
 * Format file size in bytes to human readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Format upload progress percentage
 */
export const formatProgress = (percentage: number): string => {
  return `${Math.round(percentage)}%`;
};

/**
 * Format estimated time remaining
 */
export const formatTimeRemaining = (seconds: number): string => {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${Math.round(seconds % 60)}s`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }
};

/**
 * Format date to relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'Just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString();
  }
};

/**
 * Format date to human readable format
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Format upload status to display text
 */
export const formatUploadStatus = (status: string): string => {
  switch (status.toUpperCase()) {
    case 'PENDING':
      return 'Pending';
    case 'IN_PROGRESS':
      return 'Uploading';
    case 'COMPLETED':
      return 'Completed';
    case 'FAILED':
      return 'Failed';
    case 'CANCELLED':
      return 'Cancelled';
    default:
      return status;
  }
};

/**
 * Truncate filename if too long
 */
export const truncateFilename = (filename: string, maxLength: number = 50): string => {
  if (filename.length <= maxLength) {
    return filename;
  }

  const extension = filename.split('.').pop();
  const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
  const truncatedName = nameWithoutExt.substring(0, maxLength - extension!.length - 4);
  
  return `${truncatedName}...${extension}`;
};

/**
 * Get file type icon based on MIME type
 */
export const getFileTypeIcon = (mimeType: string | undefined, isCompressed?: boolean): string => {
  // Show compression icon if file was compressed during upload
  if (isCompressed) return '🗜️';
  
  // Handle undefined or empty mimeType
  if (!mimeType) return '📁';
  
  if (mimeType.startsWith('image/')) return '🖼️';
  if (mimeType.startsWith('video/')) return '🎥';
  if (mimeType.startsWith('audio/')) return '🎵';
  if (mimeType.includes('pdf')) return '📄';
  if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
  if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return '📋';
  if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('archive')) return '🗜️';
  if (mimeType.includes('sql') || mimeType.includes('database')) return '🗄️';
  if (mimeType.includes('text/')) return '📄';
  return '📁';
};

/**
 * Format compression ratio for display
 */
export const formatCompressionRatio = (ratio: number): string => {
  if (ratio <= 1) return 'No compression';
  return `${ratio.toFixed(1)}:1`;
};

/**
 * Calculate space saved percentage from compression ratio
 */
export const calculateSpaceSaved = (compressionRatio: number): string => {
  if (!compressionRatio || compressionRatio <= 1) return '0%';
  const spaceSavedPercent = ((compressionRatio - 1) / compressionRatio) * 100;
  return `${Math.round(spaceSavedPercent)}%`;
};

/**
 * Get compression status text
 */
export const getCompressionStatus = (file: { 
  isCompressed?: boolean; 
  wasOriginallyZip?: boolean; 
  spaceSaved?: string;
  compressionRatio?: number;
}): string => {
  if (file.wasOriginallyZip) {
    return 'Already compressed';
  } else if (file.isCompressed) {
    const spaceSaved = file.spaceSaved || (file.compressionRatio ? calculateSpaceSaved(file.compressionRatio) : null);
    if (spaceSaved && spaceSaved !== '0%') {
      return `Compressed (${spaceSaved} saved)`;
    } else {
      return 'Compressed';
    }
  }
  return 'Not compressed';
};

/**
 * Generate a unique ID for upload sessions
 */
export const generateUploadId = (): string => {
  return `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Calculate upload speed in bytes per second
 */
export const calculateUploadSpeed = (bytesUploaded: number, elapsedTimeMs: number): string => {
  if (elapsedTimeMs <= 0) return '0 B/s';
  
  const bytesPerSecond = (bytesUploaded / elapsedTimeMs) * 1000;
  return `${formatFileSize(bytesPerSecond)}/s`;
};